"use client";
import React, { useCallback, useRef, useState } from "react";
import { useCurrency } from "@/contexts/UserPreferencesContext/hooks/useCurrency";
import useIsMobile from "@/hooks/useIsMobile";
import Icon from "@/components/icons";
import { cn } from "@/lib/utils";
import { ElementType } from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import {
  CURRENCY_OPTION_CLICK_HTML_ID,
  CURRENCY_OPTION_CLICK_NAME,
  CURRENCY_PILL_CLICK_HTML_ID,
  CURRENCY_PILL_CLICK_NAME,
} from "../trackingConstants";
import CurrencyDropdown from "./CurrencyDropdown";
import CurrencyBottomSheet from "./CurrencyBottomSheet";

interface CurrencyPillProps {
  location: string;
  className?: string;
  onCurrencyChange?: (code: string) => void;
}

export default function CurrencyPill({
  location,
  className,
  onCurrencyChange,
}: CurrencyPillProps) {
  const [open, setOpen] = useState(false);
  const isOpenRef = useRef(false);
  const isMobile = useIsMobile();
  const { selectedCurrency, setCurrency, getCurrencyData } = useCurrency();

  const { elementClicked: pillClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Button,
      htmlId: CURRENCY_PILL_CLICK_HTML_ID,
      location,
      name: CURRENCY_PILL_CLICK_NAME,
      text: selectedCurrency,
    },
  });

  const { elementClicked: optionClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Button,
      htmlId: CURRENCY_OPTION_CLICK_HTML_ID,
      location,
      name: CURRENCY_OPTION_CLICK_NAME,
      text: selectedCurrency,
    },
  });

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen && !isOpenRef.current) {
        pillClicked({ webElement: { text: selectedCurrency } });
      }
      isOpenRef.current = nextOpen;
      setOpen(nextOpen);
    },
    [pillClicked, selectedCurrency],
  );

  const handleSelect = useCallback(
    (code: string) => {
      optionClicked({ webElement: { text: code } });
      setCurrency(code);
      onCurrencyChange?.(code);
      handleOpenChange(false);
    },
    [setCurrency, onCurrencyChange, optionClicked, handleOpenChange],
  );

  const handleTriggerClick = useCallback(() => {
    if (isMobile) {
      handleOpenChange(!open);
    }
  }, [isMobile, open, handleOpenChange]);

  const currencyData = getCurrencyData(selectedCurrency);

  const trigger = (
    <button
      type="button"
      onClick={handleTriggerClick}
      aria-label={`Selected currency: ${selectedCurrency}. Click to change.`}
      aria-expanded={open}
      aria-haspopup={isMobile ? "dialog" : "menu"}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border-2 border-primary py-2.5 pl-4 pr-2 text-sm font-medium transition-colors hover:bg-primary hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        "min-h-11 min-w-11 md:min-h-0 md:min-w-0",
        open ? "bg-primary text-primary-foreground" : "bg-white text-primary",
        className,
      )}
    >
      {currencyData?.flag && (
        <span aria-hidden="true">{currencyData.flag}</span>
      )}
      <span>{selectedCurrency}</span>
      <Icon
        type="keyboard-arrow-down"
        svgProps={{
          width: 16,
          height: 16,
          className: cn("transition-transform", open && "rotate-180"),
        }}
      />
    </button>
  );

  if (isMobile) {
    return (
      <>
        {trigger}
        <CurrencyBottomSheet
          open={open}
          onOpenChange={handleOpenChange}
          selectedCurrency={selectedCurrency}
          onSelect={handleSelect}
        />
      </>
    );
  }

  return (
    <CurrencyDropdown
      open={open}
      onOpenChange={handleOpenChange}
      selectedCurrency={selectedCurrency}
      onSelect={handleSelect}
    >
      {trigger}
    </CurrencyDropdown>
  );
}
